import { HttpClient } from "@angular/common/http";
import { Injectable, effect, inject, signal, computed, OnDestroy } from "@angular/core";
import { Observable, timer, Subscription } from "rxjs";
import { switchMap } from "rxjs/operators";

import { API_ROOT } from "../../types/constants/appconstants";
import { NoticeWithUnreadMessageDto, UserNotification } from "../../types";
import { Notice } from "../../types/notice";
import { SignalRService } from "../signalr.service";
import { withSpinner } from "../../extensions/withSpinner.extension";

@Injectable({
  "providedIn": "root"
})
export class NoticeCenterService implements OnDestroy {
  private http = inject(HttpClient);
  private noticeServiceBaseUrl = inject(API_ROOT) + "api/notice";
  private cxMessagingServiceBaseUrl = inject(API_ROOT) + "api/cxmessaging";
  private signalrService: SignalRService = inject(SignalRService);
  public unreadMessages = signal<NoticeWithUnreadMessageDto[]>([]);
  public activeNotices = signal<Notice[]>([]);
  public hasNotices = computed(() => this.unreadMessages().length + this.activeNotices().length > 0);
  private unreadMessagesTimer?: Subscription;
  
  public allNotice = computed(() => { 
    const messages = this.unreadMessages()
    const notices = this.activeNotices();
    const allNotices: Notice[] | NoticeWithUnreadMessageDto[] = [...messages, ...notices];
    return allNotices;
  });

  constructor() {

    effect(() => {
      const cxResponse = this.signalrService.cxUnreadMessages();
      if (cxResponse) {
        this.fetchNoticeWithUnreadMessageDtos(cxResponse).subscribe(noticeDtos => {
          this.unreadMessages.update(messages => {
            
            const newNotices = new Map<number, NoticeWithUnreadMessageDto>();
            noticeDtos.forEach(msg => newNotices.set(msg.unreadMessage.lotId, msg));

            const noticeToAdd: NoticeWithUnreadMessageDto[] = [];

            messages.forEach(notice => {
              const newNotice = newNotices.get(notice.unreadMessage.lotId);
              if (newNotice) {
                notice.unreadMessage.unreadCount = newNotice.unreadMessage.unreadCount;
                notice.startDate = newNotice.startDate;
                } else {
                  noticeToAdd.push(newNotice);
                }
              }
            )

            messages = messages.filter(notice => notice.unreadMessage.unreadCount > 0);
            return messages;
          });
        });
      }
  });    
  }

  connectToCustomerPortalMessages() {
    this.signalrService.connectToCustomerPortalMessages();
  }

  startUnreadMessagesTimer() {
    const interval = 5 * 60 * 1000; // 5 minutes
    this.unreadMessagesTimer = timer(0, interval)
      .pipe(
        switchMap(() => this.getUnreadMessages())
      )
      .subscribe({
        next: (unreadMessages) => {
          this.unreadMessages.set(unreadMessages);
        }
      });
  }

  private stopUnreadMessagesTimer() {
    if (this.unreadMessagesTimer) {
      this.unreadMessagesTimer.unsubscribe();
      this.unreadMessagesTimer = undefined;
    }
  }

  public updateActiveNotices(notices: Notice[]) {
    this.activeNotices.set(notices);
  }

  public removeNotice(noticeId: number) {
    this.activeNotices.update(val => val.filter(x => x.noticeId != noticeId));
  }    


  markNoticeAsRead(noticeId: number): Observable<any> {
   return withSpinner(this.http).post(this.noticeServiceBaseUrl + `/markNoticeAsRead/${noticeId}`, {});
}

  getAllActiveNotices(): Observable<Notice[]> {
    return withSpinner(this.http).get<Notice[]>(this.noticeServiceBaseUrl + '/getallActiveNotices');
  }

  getUnreadMessages(): Observable<NoticeWithUnreadMessageDto[]> {
    return this.http.get<NoticeWithUnreadMessageDto[]>(this.cxMessagingServiceBaseUrl + '/getUnreadMessageNotices');
  }

  fetchNoticeWithUnreadMessageDtos(notification: UserNotification): Observable<NoticeWithUnreadMessageDto[]> {
    const notifications = [notification];
    return this.http.post<NoticeWithUnreadMessageDto[]>(this.cxMessagingServiceBaseUrl + '/mapSignalrMessagesToNoticeWithUnreadMessages', { notifications });
  }

  ngOnDestroy() {
    this.stopUnreadMessagesTimer();
  }
}
